import React from 'react';
import Alarm from '../../../components/svg/alarm';
import { Button } from '../../../components/mui/button';
import { color } from '../../../styles';
import { IResident } from '@/types/api/traversal/getNursingHomeResidents';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { NotificationCoverConfig } from '@/types/notificationCover';
import { IMdResident } from '@/types/api/medical/getResidents';
import { showMedicalContentSelector } from '@/redux/config/selfData';
import { useSelector } from 'react-redux';

interface Props {
  resident: IResident | IMdResident;
  config: NotificationCoverConfig;
}

const NotificationContainer = css``;

const CardBorder = (borderColor: string) => css`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 5px solid ${borderColor};
  border-radius: inherit;
  pointer-events: none;
`;

const Container = (bgColor: string) => css`
  padding: 20px 24px;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  justify-content: start;
  align-items: center;
  background-color: ${bgColor};
  border-radius: 7px;
`;

const Message = () => css`
  // 最大幅：アイコン40pxと余白12px分を除いた大きさ
  max-width: calc(100% - 52px);
  min-width: 300px;
  font-size: 20px;
  color: ${color.white};
  font-weight: bold;
`;

const IconBgWhite = css`
  position: relative;
  width: 40px;
  height: 40px;
  background-color: ${color.white};
  border-radius: 50vh;

  > img {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
  }
`;

const TimeStampContainer = css`
  flex: 1;
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 16px;
`;

const TimeStampButton = css`
  padding: 8px 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px dashed ${color.white};
  color: ${color.white};
  border-radius: 8px;
  white-space: nowrap;
`;

const MdTimeStampButton = css`
  border: none;
`;

const NotificationButton = styled(Button)`
  padding: 12px;
  flex: 0 1 120px;
  width: 120px;
  font-family: 'Noto Sans JP', 'ヒラギノ角ゴ Pro W3', 'Hiragino Kaku Gothic Pro', 'メイリオ', Meiryo, Osaka,
    'ＭＳ Ｐゴシック', 'MS PGothic', sans-serif;
  font-size: 20px;
  line-height: 1;
  font-weight: bold;
  color: ${color.dark_gray};
  border-radius: 40px;
  background: ${color.white};
  &:hover {
    background: ${color.white};
    opacity: 0.8;
  }
`;

const ImgUpsideDown = css`
  width: auto;
  height: 47px;
`;

const RightContainer = css`
  display: flex;
  align-items: center;
  column-gap: 26px;
`;

const UrineVolume = css`
  height: 44px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: ${color.white};
  border-radius: 8px;
  border: 1px dashed ${color.white};
  white-space: nowrap;
  > span {
    font-size: 16px;
  }
`;

const NotificationCover = ({ resident, config }: Props) => {
  const { t } = useTranslation();
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const notificationKey = Object.keys(resident).find((key) => resident[key as keyof typeof resident] && key in config);
  const coverInfo = config[notificationKey as keyof typeof config];

  if (coverInfo) {
    return (
      <>
        <div css={CardBorder(coverInfo.colorCode)}></div>
        <div css={NotificationContainer}>
          <div css={Container(coverInfo.colorCode)}>
            <div css={IconBgWhite}>{coverInfo.icon}</div>
            <div css={Message}>{coverInfo.message}</div>
            <div css={TimeStampContainer}>
              <div css={[TimeStampButton, showMedicalContent && MdTimeStampButton]}>
                <Alarm fillColor={color.white} />
                {resident.coverTimestamp && dayjs.utc(resident.coverTimestamp).fromNow(true)}
                {t('havePassed', '経過')}
              </div>
              {resident.upsideDownNotification && (
                <img src='/images/notification/large_icon/upside_down.svg' alt='DFreeが上下逆' css={ImgUpsideDown} />
              )}
            </div>
            <div css={RightContainer}>
              {/* 1stでは目安尿量を表示しない */}
              {/* {coverInfo.showUrineVolume &&
                showMedicalContent &&
                (resident as IMdResident)?.coverUrineVolume !== null && (
                  <div css={UrineVolume}>
                    {t('Est urine volume', '目安尿量')}
                    <span>：</span>
                    {(resident as IMdResident)?.coverUrineVolume}ml
                  </div>
                )} */}
              {coverInfo.hint}
              <NotificationButton onClick={coverInfo.buttonAction}>{coverInfo.buttonText}</NotificationButton>
            </div>
          </div>
        </div>
      </>
    );
  } else {
    return <></>;
  }
};

export default NotificationCover;
